import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button/Button.tsx"
import { Card } from "@/components/Card/Card.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { ColorPicker } from "@/components/ProjectCoverEditor/ProjectColorPicker.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { AttachRegular, DeleteLight, Icon16, Icon20, UploadLight } from "@/icons/Icons.ts"
import { smallFontSize } from "@/styles/textStyles.ts"

import type { ProjectCoverModel } from "@/components/ProjectCoverEditor/ProjectCoverModel.ts"
import type { ProjectGeneralTabModel } from "@/components/ProjectSettingsDialog/ProjectGeneralTabModel.ts"


export const ProjectCoverEditor = observer(({ model, canEditProject }: {
  model: ProjectGeneralTabModel
  canEditProject: boolean
}) => {
  const showCover = model.coverModel.showCover.get()
  const hasError = model.coverModel.dropZoneModel.hasError.get()

  return (
    <CoverTabContainer>
      <Section>
        <Heading2>Project Cover</Heading2>

        <SectionDescription>
          Recommended size: 620×360px. Supported formats: JPG, PNG, GIF.
        </SectionDescription>

        <CoverContainer>
          {showCover
            ? <ViewMode model={model.coverModel} canEditProject={canEditProject} />
            : <DropMode model={model.coverModel} canEditProject={canEditProject} />
          }
        </CoverContainer>

        {hasError && (
          <ErrorMessage>
            Please select a valid image file (JPG, PNG, or GIF).
          </ErrorMessage>
        )}
      </Section>

      <Section>
        <Heading2>Project Color</Heading2>

        <SectionDescription>
          This color will be used when no cover image is set.
        </SectionDescription>

        <ColorPickerCard>
          <ColorPicker model={model.coverModel} canEditProject={canEditProject} />
        </ColorPickerCard>
      </Section>

    </CoverTabContainer>
  )
})

const ViewMode = observer((props: { model: ProjectCoverModel, canEditProject: boolean }) => {
  const model = props.model
  const cover = model.cover.get()
  const isLoading = model.isLoading.get()

  if (isLoading) {
    return (
      <LoadingOverlay>
        <Spinner />
      </LoadingOverlay>
    )
  }

  return (
    <CoverPreview>
      <CoverImage src={cover!} alt="Project cover" />

      {props.canEditProject && (
        <CoverOverlay>
          <Horizontal alignItems="center" gap="8px">
            <ChangeCoverButton
              kind="alternative"
              onClick={model.dropZoneModel.onClick}
              disabled={isLoading}
            >
              <UploadLight className={Icon20} />
              Change cover
            </ChangeCoverButton>

            <RemoveCoverButton
              kind="icon-rounded"
              onClick={model.removeCover}
              disabled={isLoading}
              title="Remove cover"
            >
              <DeleteLight className={Icon20} />
            </RemoveCoverButton>
          </Horizontal>
        </CoverOverlay>
      )}
    </CoverPreview>
  )
})

const DropMode = observer((props: {
  model: ProjectCoverModel
  canEditProject: boolean
}) => {
  const isDragging = props.model.dropZoneModel.isDragOver.get()
  const hasError = props.model.dropZoneModel.hasError.get()

  return (
    <DropContainer
      ref={props.model.dropZoneModel.refCallback}
      canEditProject={props.canEditProject}
      isDragging={isDragging}
      hasError={hasError}
    >
      <Horizontal alignItems="center" gap="4px">
        <AttachRegular className={Icon16} />
        <DropMessage>
          <DropContent model={props.model} canEditProject={props.canEditProject} />
        </DropMessage>
      </Horizontal>
    </DropContainer>
  )
})

const DropContent = observer((props: {
  model: ProjectCoverModel
  canEditProject: boolean
}) => {
  const model = props.model
  const hasError = model.dropZoneModel.hasError.get()
  const isDragging = model.dropZoneModel.isDragOver.get()

  if (hasError) {
    return "Invalid file type"
  }

  if (isDragging) {
    return "Drop an image here"
  }

  if (!props.canEditProject) {
    return "You don't have permission to change the cover image"
  }

  return "Drop an image here or click to browse"
})

const CoverTabContainer = styled(Vertical)`
  flex: 1;
  gap: 32px;

  max-height: 100%;
`

const Section = styled(Vertical)`
  gap: 16px;
`

const SectionDescription = styled.p`
  margin: 0;
  color: var(--secondary-text-color);
  font-size: 14px;
  line-height: 1.4;
`

const CoverContainer = styled(Horizontal)`
  position: relative;
  overflow: hidden;

  aspect-ratio: 620 / 360;

  border-radius: var(--border-radius-8);
`

const CoverPreview = styled.div`
  position: absolute;
  inset: 0;

  background-color: var(--separator-color);
`

const CoverImage = styled.img`
  display: block;

  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`

const CoverOverlay = styled.div`
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  opacity: 0;
  background: rgb(0 0 0 / 60%);

  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 1;
  }
`

const ChangeCoverButton = styled(Button)`
  gap: 4px;

  background: #fff;

  &:hover:not(:disabled) {
    background: #e8e8e8 !important;
  }
`

const RemoveCoverButton = styled(Button)`
  color: var(--text-color);
  background: #fff;

  &:hover:not(:disabled) {
    background: #e8e8e8 !important;
  }
`

const DropContainer = styled.div<{
  canEditProject: boolean
  isDragging?: boolean
  hasError?: boolean
}>`
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${props =>
    props.hasError ? "var(--error-color)" :
      props.isDragging ? "var(--main-color)" : "var(--secondary-text-color)"};

  background: ${props =>
    props.isDragging ? "var(--main-color-5)" : "var(--island-background-color)"};

  border: 2px dashed ${props =>
    props.hasError ? "var(--error-color)" :
      props.isDragging ? "var(--main-color)" : "var(--separator-color)"};

  border-radius: var(--border-radius-8);

  cursor: pointer;
  transition: all 0.2s ease-in-out;

  pointer-events: ${props => props.canEditProject ? "auto" : "none"};

  &:hover {
    color: ${props => props.hasError ? "var(--error-color)" : "var(--text-color)"};
    border-color: ${props => props.hasError ? "var(--error-color)" : "var(--main-color)"};
    background: ${props => props.hasError ? "var(--error-color-10)" : "var(--main-color-5)"};
  }
`

const DropMessage = styled.span`
  ${smallFontSize};
`

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: var(--background-color-40);
  backdrop-filter: blur(4px);
`

const ErrorMessage = styled.div`
  ${smallFontSize};

  color: var(--error-color);
`

const ColorPickerCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  padding: 20px !important;
  margin-bottom: 16px;
`